import { useCallback, useEffect, useMemo, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import ConfirmModal from "../../components/admin/ConfirmModal";
import RefreshButton from "../../components/admin/RefreshButton";
import { useAdminToast } from "../../context/AdminToastContext";
import { subscriptionsService } from "../../features/subscriptions/service";
import type { ConfigEntry } from "../../features/subscriptions/types";
import {
  featureOf,
  formatSeconds,
  limitConflicts,
  lockedDailyCaps,
  parseLimitKey,
  secondsToUnit,
  sortByFeature,
  unitToSeconds,
  type TimeUnit,
} from "../../features/subscriptions/planLimitsModel";
import { parseApiError } from "../../lib/http/apiErrors";
import { isPriceOrSwitchKey } from "./AdminSubscriptionsPage";

type Role = "seller" | "buyer";
type Tab = "monthly" | "daily" | "chat" | "other";

const ROLES: { key: Role; label: string }[] = [
  { key: "seller", label: "Seller" },
  { key: "buyer", label: "Buyer" },
];

const TABS: { key: Tab; label: string; blurb: string }[] = [
  { key: "monthly", label: "Monthly allowance", blurb: "What each plan includes per month. This is the plan's product limit." },
  { key: "daily", label: "Daily safety caps", blurb: "An extra cap per day that sits on top of the monthly allowance. Mostly there to stop abuse." },
  { key: "chat", label: "Chat messages", blurb: "How many messages a user can send inside a single chat." },
  { key: "other", label: "Credits & other", blurb: "Credit prices for pay-per-use, low-limit warnings and subscription timing." },
];

const TIERS: Record<Role, { key: string; label: string }[]> = {
  seller: [
    { key: "free", label: "Free" },
    { key: "starter", label: "Starter" },
    { key: "mid", label: "Mid" },
    { key: "premium", label: "Premium" },
  ],
  buyer: [
    { key: "free", label: "Free" },
    { key: "pro", label: "Pro" },
    { key: "studio", label: "Studio" },
    { key: "elite", label: "Elite" },
  ],
};

function valueBadge(v: number | null, seconds: boolean) {
  if (v === null) return null;
  const text = seconds ? formatSeconds(v) : v < 0 ? "Unlimited" : v === 0 ? "Not included" : String(v);
  const tone = v < 0 ? "bg-green-900/30 text-green-300" : v === 0 ? "bg-red-900/30 text-red-300" : "bg-gray-800 text-gray-300";
  return <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${tone}`}>{text}</span>;
}

const OTHER_GROUPS: { title: string; match: (k: string) => boolean }[] = [
  { title: "Video and generation credit pricing", match: (k) => k.startsWith("VIDEO_") || k.startsWith("GENERATION_CREDIT_COST_") },
  { title: "Low-limit warnings", match: (k) => k.startsWith("QUOTA_LOW_WARNING_THRESHOLD_") },
  { title: "Subscription timing", match: () => true },
];

/** Billing → Plan Limits: what each plan may use. Prices and feature switches
 *  live on Plans & Prices. */
export default function AdminPlanLimitsPage() {
  const [configs, setConfigs] = useState<ConfigEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<Role>("seller");
  const [tab, setTab] = useState<Tab>("monthly");
  const [tier, setTier] = useState("free");
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [units, setUnits] = useState<Record<string, TimeUnit>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [resetTarget, setResetTarget] = useState<string | null>(null);
  const { showError, showSuccess } = useAdminToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await subscriptionsService.adminGetConfig();
      const rows = res.configs.filter((c) => !isPriceOrSwitchKey(c.key));
      setConfigs(rows);
      // A daily cap held at 0 by its monthly limit starts as 0, so a leftover
      // higher value shows as an unsaved change the admin can fix with Save.
      const locked = lockedDailyCaps(rows.filter((c) => c.role === "seller")) ;
      const lockedBuyer = lockedDailyCaps(rows.filter((c) => c.role === "buyer"));
      setDrafts(Object.fromEntries(rows.map((c) => [c.key, locked[c.key] || lockedBuyer[c.key] ? "0" : String(c.effective_value ?? "")])));
    } catch (err) {
      showError(parseApiError(err).message);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => { load(); }, [load]);

  const roleRows = useMemo(() => configs.filter((c) => c.role === role), [configs, role]);
  const conflicts = useMemo(() => limitConflicts(roleRows), [roleRows]);
  const locked = useMemo(() => lockedDailyCaps(roleRows), [roleRows]);
  const dirty = configs.filter((c) => drafts[c.key] !== String(c.effective_value ?? ""));

  const save = async (keys: string[]) => {
    const parsed = keys.map((key) => ({ key, val: parseInt(drafts[key] ?? "", 10) }));
    if (parsed.some((p) => Number.isNaN(p.val) || p.val < -1)) {
      showError("Enter -1 (unlimited), 0 (not included), or a positive number.");
      return;
    }
    setSaving(keys.length === 1 ? keys[0] : "__all__");
    try {
      const results = await Promise.allSettled(parsed.map((p) => subscriptionsService.adminSetConfig(p.key, p.val)));
      const rejected = results.filter((r): r is PromiseRejectedResult => r.status === "rejected");
      if (rejected.length) {
        const why = parseApiError(rejected[0].reason).message;
        showError(rejected.length === 1 && parsed.length === 1 ? why : `${rejected.length} of ${parsed.length} limits failed to save. ${why}`);
      }
      else {
        const extra = results.flatMap((r) => (r.status === "fulfilled" ? [(r.value as { message?: string })?.message ?? ""] : []))
          .find((m) => m.includes("daily cap set to 0"));
        showSuccess(extra ?? (parsed.length === 1 ? "Limit saved. It applies within about 30 seconds." : `Saved ${parsed.length} limits.`));
      }
      await load();
    } finally {
      setSaving(null);
    }
  };

  const reset = async (key: string) => {
    setSaving(key);
    try {
      await subscriptionsService.adminResetConfig(key);
      showSuccess("Back to the default.");
      await load();
    } catch (err) {
      showError(parseApiError(err).message);
    } finally {
      setSaving(null);
    }
  };

  const row = (cfg: ConfigEntry, opts: { label: string; help: string; seconds?: boolean }) => {
    const isOverridden = cfg.override_value !== null;
    const unit = units[cfg.key] ?? "sec";
    const stored = parseInt(drafts[cfg.key] ?? "", 10);
    const shown = opts.seconds && !Number.isNaN(stored) ? secondsToUnit(stored, unit) : drafts[cfg.key] ?? "";
    const warn = conflicts[cfg.key];
    const lockReason = locked[cfg.key];
    return (
      <div key={cfg.key} className="px-5 py-3.5">
        <div className="flex items-center gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm text-white font-medium">{opts.label}</p>
              {valueBadge(cfg.effective_value, !!opts.seconds)}
              {isOverridden && <span className="text-xs text-amber-400 bg-amber-900/30 px-1.5 py-0.5 rounded">changed from default</span>}
            </div>
            <p className="text-xs text-gray-500 mt-0.5" title={cfg.description}>
              {opts.help}
              <span className="text-gray-600"> · default {opts.seconds ? formatSeconds(cfg.default_value) : cfg.default_value === null ? "—" : cfg.default_value < 0 ? "Unlimited" : cfg.default_value === 0 ? "Not included" : cfg.default_value}</span>
            </p>
          </div>
          <input
            type="number" min={opts.seconds && unit === "min" ? -1 : -1} step={opts.seconds && unit === "min" ? "0.5" : "1"}
            value={shown}
            disabled={saving === "__all__" || !!lockReason}
            onChange={(e) =>
              setDrafts((p) => ({
                ...p,
                [cfg.key]: String(opts.seconds ? unitToSeconds(e.target.value, unit) : e.target.value),
              }))
            }
            title={lockReason ?? "-1 = unlimited, 0 = not included"}
            className="w-24 px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50 disabled:opacity-50"
          />
          {opts.seconds && (
            <select
              value={unit}
              onChange={(e) => setUnits((p) => ({ ...p, [cfg.key]: e.target.value as TimeUnit }))}
              className="px-2 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-xs"
            >
              <option value="sec">seconds</option>
              <option value="min">minutes</option>
            </select>
          )}
          <button
            onClick={() => save([cfg.key])}
            disabled={saving !== null || drafts[cfg.key] === String(cfg.effective_value ?? "")}
            className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-xs font-medium transition-colors disabled:opacity-40 whitespace-nowrap"
          >
            {saving === cfg.key ? "…" : "Save"}
          </button>
          <button
            onClick={() => setResetTarget(cfg.key)}
            disabled={!isOverridden || saving !== null}
            className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-xs transition-colors disabled:opacity-30"
          >
            Reset
          </button>
        </div>
        {lockReason && (
          <p className="mt-2 text-xs text-gray-300 bg-gray-800/60 border border-gray-700 rounded-lg px-3 py-1.5">
            🔒 {lockReason} Raise the monthly limit first to set a daily cap.
          </p>
        )}
        {warn && !lockReason && (
          <p className="mt-2 text-xs text-amber-300 bg-amber-900/20 border border-amber-800/60 rounded-lg px-3 py-1.5">⚠ {warn}</p>
        )}
      </div>
    );
  };

  const featureRows = (rows: ConfigEntry[]) =>
    sortByFeature(rows).map((cfg) => {
      const p = parseLimitKey(cfg.key);
      const f = featureOf(p?.action ?? "");
      return row(cfg, { label: f.label, help: f.help, seconds: p?.action === "video_seconds" });
    });

  const panel = (title: string, note: string | null, body: React.ReactNode) => (
    <div className="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-800">
        <p className="text-sm font-semibold text-white">{title}</p>
        {note && <p className="text-xs text-gray-500 mt-0.5">{note}</p>}
      </div>
      <div className="divide-y divide-gray-800">{body}</div>
    </div>
  );

  const tierChips = (
    <div className="flex gap-2 flex-wrap">
      {TIERS[role].map((t) => (
        <button
          key={t.key}
          onClick={() => setTier(t.key)}
          className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold border-2 transition-colors ${
            tier === t.key ? "border-indigo-500 bg-indigo-950 text-white" : "border-gray-700 bg-gray-900 text-gray-400 hover:text-gray-200"
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );

  const body = () => {
    const byPeriod = (period: string, wantTier?: string | null) =>
      roleRows.filter((c) => {
        const p = parseLimitKey(c.key);
        if (!p || p.period !== period) return false;
        return wantTier === undefined ? true : (c.tier ?? null) === wantTier;
      });

    if (tab === "monthly") {
      const rows = byPeriod("monthly", tier);
      return (
        <>
          {tierChips}
          {panel(
            `${TIERS[role].find((t) => t.key === tier)?.label} plan: monthly allowance`,
            "-1 = unlimited · 0 = not included. When a monthly limit is used up, the user pays credits for each extra one.",
            rows.length ? featureRows(rows) : <p className="px-5 py-8 text-center text-gray-500 text-sm">Nothing to set for this plan.</p>,
          )}
        </>
      );
    }

    if (tab === "daily") {
      const all = byPeriod("daily", null);
      const tierSpecific = byPeriod("daily").filter((c) => c.tier);
      return (
        <>
          {panel(
            "Every plan: daily safety cap",
            "Applies to all tiers of this role. The first limit reached, daily or monthly, stops free use for that action.",
            all.length ? featureRows(all) : <p className="px-5 py-8 text-center text-gray-500 text-sm">No daily caps for this role.</p>,
          )}
          {tierSpecific.length > 0 &&
            panel(
              "Free plan only: extra daily cap",
              "Stricter than the cap above, for the free plan alone.",
              featureRows(tierSpecific),
            )}
        </>
      );
    }

    if (tab === "chat") {
      const rows = byPeriod("chat", tier);
      return (
        <>
          {tierChips}
          {panel("Messages per chat", "Counted within one chat session.", rows.length ? featureRows(rows) : <p className="px-5 py-8 text-center text-gray-500 text-sm">Nothing to set for this plan.</p>)}
        </>
      );
    }

    const system = configs.filter((c) => c.role === "system" && !parseLimitKey(c.key));
    const used = new Set<string>();
    return (
      <>
        {OTHER_GROUPS.map((g) => {
          const rows = system.filter((c) => !used.has(c.key) && g.match(c.key));
          rows.forEach((c) => used.add(c.key));
          if (!rows.length) return null;
          return panel(
            g.title,
            null,
            rows.map((cfg) => row(cfg, { label: cfg.key.replace(/_/g, " ").toLowerCase(), help: cfg.description })),
          );
        })}
      </>
    );
  };

  return (
    <div className="space-y-4">
      <AdminPageHeader
        title="Plan Limits"
        subtitle="What each plan may use. Prices and feature switches are on Plans & Prices."
      />

      <div className="text-xs text-gray-400 bg-gray-900 border border-gray-800 rounded-xl px-4 py-3 leading-relaxed">
        <span className="text-gray-200 font-medium">How limits work:</span> each action has a monthly allowance from the plan and an optional daily
        safety cap. Whichever is reached first stops free use; after that the user pays credits if they have them. A monthly limit of 0 means the
        plan includes none, so every one is paid with credits and a daily cap on top of it changes nothing.
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex bg-gray-900 rounded-xl p-1 gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${
                tab === t.key ? "bg-indigo-600 text-white" : "text-gray-400 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <RefreshButton onRefresh={load} />
          <button
            onClick={() => save(dirty.map((c) => c.key))}
            disabled={dirty.length === 0 || saving !== null}
            className="px-3 py-1.5 bg-green-600 hover:bg-green-500 rounded-lg text-xs font-medium transition-colors disabled:opacity-40 whitespace-nowrap"
          >
            {saving === "__all__" ? "Saving…" : `Save all${dirty.length ? ` (${dirty.length})` : ""}`}
          </button>
          {tab !== "other" && (
            <div className="flex bg-gray-900 rounded-xl p-1 gap-1">
              {ROLES.map((r) => (
                <button
                  key={r.key}
                  onClick={() => { setRole(r.key); setTier("free"); }}
                  className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    role === r.key ? "bg-indigo-600 text-white" : "text-gray-400 hover:text-white"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <p className="text-xs text-gray-500">{TABS.find((t) => t.key === tab)?.blurb}</p>

      {loading ? <div className="text-gray-400 text-sm py-4">Loading limits…</div> : <div className="space-y-4">{body()}</div>}

      {resetTarget && (
        <ConfirmModal
          title="Reset to default"
          description="Put this limit back to its default value?"
          items={["The saved change is removed", "The plan goes back to the default limit"]}
          confirmLabel="Reset to default"
          confirmClass="bg-amber-600 hover:bg-amber-500"
          onConfirm={() => { const k = resetTarget; setResetTarget(null); reset(k); }}
          onCancel={() => setResetTarget(null)}
        />
      )}
    </div>
  );
}
